import React, { useState, useEffect } from 'react';
import {
  ArrowLeft, Clock, Share2, Heart,
  Type, MessageCircle, Minus, Plus, BookOpen, FileText, ExternalLink
} from 'lucide-react';

const DiaryEntrySection = ({
  selectedDiaryEntry,
  handleNavigate,
  toggleDiaryLike,
  likedDiaryEntries,
  handleShareDiary,
  copiedLink,
  textSizeMultiplier,
  setTextSizeMultiplier,
  theme,
  isDarkMode,
  backToBook = null
}) => {
  // If backToBook is provided, back button goes to book-timeline instead of bookshelf
  const [scrollProgress, setScrollProgress] = useState(0);

  const handleScroll = () => {
    const totalHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
    if (totalHeight > 0) {
      const scrollPosition = window.scrollY;
      const progress = (scrollPosition / totalHeight) * 100;
      setScrollProgress(progress);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const pillStyle = { borderRadius: '9999px' };
  const entry = selectedDiaryEntry;

  const formatReadableDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const parts = dateStr.split('-');
      if (parts.length === 3 && parts[0].length === 4) {
        const year = parts[0];
        const monthNum = parseInt(parts[1], 10) - 1;
        const day = parseInt(parts[2], 10);
        const malayMonths = ['Januari', 'Februari', 'Mac', 'April', 'Mei', 'Jun',
          'Julai', 'Ogos', 'September', 'Oktober', 'November', 'Disember'];
        return `${day} ${malayMonths[monthNum]} ${year}`;
      }
      const d = new Date(dateStr);
      if (!isNaN(d.getTime())) {
        return d.toLocaleDateString('ms-MY', { day: 'numeric', month: 'long', year: 'numeric' });
      }
    } catch (e) { /* ignore */ }
    return dateStr;
  };

  const handleWhatsAppShare = () => {
    const url = `${window.location.origin}/?d=${entry.id}`;
    const shareText = `*${entry.title}*\n\n${url}`;
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`, '_blank');
  };

  if (!entry) return null;

  return (
    <section className="pt-32 pb-24 px-4 md:px-6 min-h-screen animate-in fade-in slide-in-from-bottom-4 duration-1000">
      {/* Reading Progress Bar */}
      <div className="fixed top-0 left-0 w-full h-0.5 z-[101]">
        <div
          className={`h-full transition-all duration-100 ease-linear ${isDarkMode ? 'bg-white' : 'bg-stone-900'}`}
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      <div className="max-w-3xl mx-auto w-full">
        {/* Back Button */}
        <div className="flex items-center justify-between mb-16">
          <button
            onClick={() => backToBook ? handleNavigate('book-timeline', backToBook) : handleNavigate('diary')}
            className={`flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] opacity-60 hover:opacity-100 transition-all ${theme.text}`}
          >
            <ArrowLeft size={14} /> {backToBook ? 'Back to Timeline' : 'Reading Diary'}
          </button>
        </div>

        {/* Header */}
        <header className="space-y-10 mb-10">
          <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-4">
              {/* Type Pill */}
              <div style={pillStyle} className={`inline-flex px-4 py-1.5 border text-[9px] font-bold uppercase tracking-widest transition-colors items-center gap-2 ${
                isDarkMode ? 'bg-transparent border-stone-800 text-stone-500' : 'bg-transparent border-stone-100 text-stone-400'
              }`}>
                {entry.type === 'book' ? <BookOpen size={11} /> : <FileText size={11} />}
                {entry.type === 'book' ? 'Book' : 'Article'}
              </div>
              {/* Category Pill */}
              {entry.category && (
                <div style={pillStyle} className={`inline-flex px-4 py-1.5 border text-[9px] font-bold uppercase tracking-widest transition-colors ${
                  isDarkMode ? 'bg-transparent border-stone-800 text-stone-500' : 'bg-transparent border-stone-100 text-stone-400'
                }`}>
                  {entry.category}
                </div>
              )}
              {/* Topic Badge */}
              {entry.topic && (
                <div style={pillStyle} className={`inline-flex px-4 py-1.5 border text-[9px] font-bold uppercase tracking-widest transition-colors ${
                  isDarkMode ? 'bg-transparent border-stone-800 text-stone-500' : 'bg-transparent border-stone-100 text-stone-400'
                }`}>
                  {entry.topic}
                </div>
              )}
            </div>
            <h1 className={`text-5xl md:text-7xl font-garamond leading-[1.05] tracking-tight ${theme.text}`}>
              {entry.title}
            </h1>
            {entry.author && (
              <p className={`text-sm font-semibold uppercase tracking-[0.1em] opacity-40 ${theme.text}`}>
                By {entry.author}
              </p>
            )}
          </div>

          <div className={`flex flex-wrap items-center gap-10 text-[10px] font-bold uppercase tracking-[0.25em] opacity-80 ${theme.text}`}>
            <span className="flex items-center gap-2 whitespace-nowrap">{formatReadableDate(entry.date)}</span>
            <span className="flex items-center gap-2 whitespace-nowrap">
              <Clock size={12} /> {entry.readTime || '1 MIN READ'}
            </span>
            {entry.link && (
              <a href={entry.link} target="_blank" rel="noopener noreferrer"
                className={`flex items-center gap-2 opacity-60 hover:opacity-100 transition-opacity ${theme.text}`}>
                <ExternalLink size={12} /> Reading Link
              </a>
            )}
          </div>
        </header>

        {/* Action Bar */}
        <div className={`flex flex-wrap items-center justify-between gap-6 mb-16 py-6 border-y ${
          isDarkMode ? 'border-stone-800' : 'border-stone-100'
        }`}>
          <div className="flex items-center gap-8">
            {/* Like Button */}
            <button
              onClick={() => toggleDiaryLike(entry.id)}
              className={`group flex items-center gap-3 transition-all ${theme.text}`}
            >
              <div className={`p-2 rounded-full border transition-all ${
                likedDiaryEntries && likedDiaryEntries[entry.id]
                  ? 'bg-red-500/5 border-red-500 text-red-500'
                  : `border-transparent ${isDarkMode ? 'hover:bg-stone-800' : 'hover:bg-stone-100'} opacity-80 group-hover:opacity-100`
              }`}>
                <Heart size={18} strokeWidth={2} fill={likedDiaryEntries && likedDiaryEntries[entry.id] ? 'currentColor' : 'none'} />
              </div>
              <span className={`text-[10px] font-bold uppercase tracking-widest transition-opacity ${
                likedDiaryEntries && likedDiaryEntries[entry.id] ? 'opacity-100' : 'opacity-80 group-hover:opacity-100'
              }`}>
                {likedDiaryEntries && likedDiaryEntries[entry.id] ? 'Liked' : 'Like'}
              </span>
            </button>

            {/* Share Button */}
            <button
              onClick={handleShareDiary}
              className={`group flex items-center gap-3 transition-all ${theme.text}`}
            >
              <div className={`p-2 rounded-full border border-transparent transition-all ${
                isDarkMode ? 'hover:bg-stone-800' : 'hover:bg-stone-100'
              } opacity-80 group-hover:opacity-100`}>
                <Share2 size={18} strokeWidth={2} />
              </div>
              <span className="text-[10px] font-bold uppercase tracking-widest opacity-80 group-hover:opacity-100">Share</span>
            </button>

            {/* WhatsApp Button */}
            <button
              onClick={handleWhatsAppShare}
              className={`group flex items-center gap-3 transition-all ${theme.text}`}
            >
              <div className={`p-2 rounded-full border border-transparent transition-all ${
                isDarkMode ? 'hover:bg-stone-800' : 'hover:bg-stone-100'
              } opacity-80 group-hover:opacity-100`}>
                <MessageCircle size={18} strokeWidth={2} />
              </div>
              <span className="text-[10px] font-bold uppercase tracking-widest opacity-80 group-hover:opacity-100">WhatsApp</span>
            </button>
          </div>

          {/* Text Size Control */}
          <div style={pillStyle} className={`flex items-center border ${isDarkMode ? 'border-stone-800' : 'border-stone-100'}`}>
            <button onClick={() => setTextSizeMultiplier(prev => Math.max(0.8, prev - 0.1))}
              className={`p-3 hover:opacity-50 transition-opacity ${theme.text}`} title="Decrease Text Size">
              <Minus size={14} />
            </button>
            <div className={`w-px h-3 ${isDarkMode ? 'bg-stone-800' : 'bg-stone-100'}`} />
            <div className={`px-3 ${theme.text} opacity-50`}><Type size={14} /></div>
            <div className={`w-px h-3 ${isDarkMode ? 'bg-stone-800' : 'bg-stone-100'}`} />
            <button onClick={() => setTextSizeMultiplier(prev => Math.min(2, prev + 0.1))}
              className={`p-3 hover:opacity-50 transition-opacity ${theme.text}`} title="Increase Text Size">
              <Plus size={14} />
            </button>
          </div>
        </div>

        {/* Content */}
        <style>{`
          ${isDarkMode ? `
          .enforce-prose-colors {
            --tw-prose-body: #ffffff !important;
            --tw-prose-headings: #ffffff !important;
            --tw-prose-bold: #ffffff !important;
            --tw-prose-quotes: #e7e5e4 !important;
            --tw-prose-counters: #ffffff !important;
            --tw-prose-bullets: #ffffff !important;
          }
          .enforce-prose-colors li::marker { color: #ffffff !important; }
          ` : `
          .enforce-prose-colors {
            --tw-prose-counters: #000000 !important;
            --tw-prose-bullets: #000000 !important;
          }
          .enforce-prose-colors li::marker { color: #000000 !important; }
          `}
          .enforce-prose-colors * { background-color: transparent !important; }
          .enforce-prose-colors pre { background-color: var(--tw-prose-pre-bg) !important; }
          .enforce-prose-colors p, .enforce-prose-colors span, .enforce-prose-colors div,
          .enforce-prose-colors li { color: var(--tw-prose-body) !important; }
          .enforce-prose-colors h1, .enforce-prose-colors h2, .enforce-prose-colors h3,
          .enforce-prose-colors h4, .enforce-prose-colors h5, .enforce-prose-colors h6 { color: var(--tw-prose-headings) !important; }
          .enforce-prose-colors a { color: var(--tw-prose-links) !important; }
          .enforce-prose-colors strong, .enforce-prose-colors b { color: var(--tw-prose-bold) !important; }
          .enforce-prose-colors blockquote { color: var(--tw-prose-quotes) !important; }
          .enforce-prose-colors code { color: var(--tw-prose-code) !important; }
          .enforce-prose-colors pre code { color: var(--tw-prose-pre-code) !important; background-color: transparent !important; }
        `}</style>

        <article
          className={`prose prose-stone dark:prose-invert max-w-none font-garamond leading-loose selection:bg-stone-200 dark:selection:bg-stone-800 ${theme.text} enforce-prose-colors`}
          style={{ fontSize: `${textSizeMultiplier * 1.375}rem` }}
          dangerouslySetInnerHTML={{ __html: entry.content }}
        />

        {/* Footer */}
        <footer className="mt-20">
          {copiedLink && (
            <div className="fixed bottom-12 left-1/2 -translate-x-1/2 animate-in slide-in-from-bottom-6 duration-700 z-[100]">
              <div className="px-8 py-4 bg-stone-900 text-white rounded-full text-[10px] font-bold uppercase tracking-widest shadow-2xl backdrop-blur-xl">
                Sanctuary link copied to clipboard
              </div>
            </div>
          )}
        </footer>
      </div>
    </section>
  );
};

export default DiaryEntrySection;
